import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../contexts/AppContext";
import { paletteFromColor } from "./ClayCard";
import { toast } from "sonner";
import { Plus, Eye } from "lucide-react";

const clampN = (v, min, max, fb) => { const n = Number(v); return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fb; };

export const EMPTY_QUESTION = {
  identification: "",
  image_path: "",
  question_pt: "",
  question_en: "",
  question_br: "",
  options_pt: ["", "", "", "", ""],
  options_en: ["", "", "", "", ""],
  options_br: ["", "", "", "", ""],
  correct_index: 0,
  max_time_seconds: 60,
  penalty_seconds: 10,
  active: true,
};

export default function QuestionForm({ initial, onSubmit, submitLabel = "Adicionar pergunta" }) {
  const { settingsRaw: settings } = useApp();
  const [form, setForm] = useState({ ...EMPTY_QUESTION, ...(initial || {}) });
  const [uploading, setUploading] = useState(false);
  const [transLang, setTransLang] = useState("en");
  const [pvLang, setPvLang] = useState("pt");
  const fileRef = useRef(null);

  useEffect(() => { setForm({ ...EMPTY_QUESTION, ...(initial || {}) }); }, [initial]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Imagem carregada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload falhou");
    } finally {
      setUploading(false);
    }
  };

  const setOpt = (lg, i, v) => {
    const key = `options_${lg}`;
    const arr = [...(form[key] || [])];
    while (arr.length < (form.options_pt || []).length) arr.push("");
    arr[i] = v;
    setForm({ ...form, [key]: arr });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.image_path) { toast.error("Carrega uma imagem primeiro"); return; }
    onSubmit(form).then(() => {
      if (!initial) { setForm(EMPTY_QUESTION); if (fileRef.current) fileRef.current.value = ""; }
    }).catch(() => {});
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const pvWidthPct = clampN(settings?.quiz_image_width_pct, 20, 100, 70);
  const pvQuestionPx = clampN(settings?.quiz_question_text_px, 12, 48, 22);
  const pvAnswerPx = clampN(settings?.quiz_answer_text_px, 12, 36, 16);
  const pvAspect = settings?.quiz_image_aspect || "auto";
  const pvFit = settings?.quiz_image_fit === "cover" ? "cover" : "contain";
  const pvFixed = pvAspect !== "auto";
  const pvAnsFormat = settings?.quiz_answer_format || "soft";
  const pvAnsEffect = settings?.quiz_answer_effect || "lift";
  const pvAnsFont = settings?.quiz_answer_font || "fredoka";
  const pvAnsPalette = paletteFromColor(settings?.quiz_answer_color || "snow");
  const pvImageUrl = form.image_path ? `${backendUrl}/api/files/${form.image_path}` : null;
  const pvIsEn = pvLang === "en";
  const pvQuestionText = pvLang === "en" ? form.question_en : (pvLang === "br" ? (form.question_br || form.question_pt) : form.question_pt);
  const pvOptsSrc = pvLang === "en" ? (form.options_en || []) : (pvLang === "br" ? (form.options_pt || []).map((o, i) => (form.options_br && form.options_br[i]) || o) : (form.options_pt || []));
  const pvOptions = pvOptsSrc.map((o, i) => ({ o, i })).filter(({ o }) => o && String(o).trim());

  return (
    <div>
      <form onSubmit={submit} className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 mb-6 space-y-4" data-testid="question-form">
        <div>
          <label className="block text-sm font-semibold mb-1">Identificação <span className="font-normal text-[#0B1D3A]/50">(só no backoffice, nunca aparece no site)</span></label>
          <input value={form.identification || ""} onChange={(e) => setForm({ ...form, identification: e.target.value })} placeholder="ex.: Soma nível 1 — nº 3" className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="question-identification-input" />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">Imagem da pergunta</label>
          <div className="flex items-center gap-4">
            <input type="file" ref={fileRef} accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} className="text-sm" data-testid="question-image-input" />
            {uploading && <span className="text-sm text-[#0B1D3A]/60">A carregar...</span>}
            {form.image_path && <img src={`${backendUrl}/api/files/${form.image_path}`} alt="" className="h-16 w-16 object-cover rounded-lg border" />}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-[#0B1D3A]/50">PT é a base. A tradução (BR/EN) é opcional — se vazia, o site usa o PT.</p>
          <div className="inline-flex rounded-full border border-[#0B1D3A]/15 overflow-hidden text-xs font-bold" data-testid="question-trans-lang">
            {[["en", "EN"], ["br", "BR"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setTransLang(v)} data-testid={`question-trans-lang-${v}`}
                className={`px-3 py-1 ${transLang === v ? "bg-[#005F73] text-white" : "bg-white text-[#0B1D3A]/60"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <textarea placeholder="Pergunta (PT)" value={form.question_pt} onChange={(e) => setForm({ ...form, question_pt: e.target.value })} required className="px-3 py-2 rounded-lg border border-[#0B1D3A]/15" rows="2" data-testid="question-pt-input" />
          <textarea placeholder={`Pergunta (${transLang.toUpperCase()})`} value={form[`question_${transLang}`] || ""} onChange={(e) => setForm({ ...form, [`question_${transLang}`]: e.target.value })} required={transLang === "en"} className="px-3 py-2 rounded-lg border border-[#0B1D3A]/15" rows="2" data-testid="question-en-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-sm font-semibold">Opções (PT) - escolhe a correta</p>
            <p className="text-xs text-[#0B1D3A]/50">Deixa em branco as que não usares (mínimo 2 opções).</p>
            {form.options_pt.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="radio" name="correct" checked={form.correct_index === i} onChange={() => setForm({ ...form, correct_index: i })} data-testid={`correct-radio-${i}`} />
                <input value={opt} onChange={(e) => setOpt("pt", i, e.target.value)} placeholder={`Opção ${String.fromCharCode(65 + i)} (opcional)`} className="flex-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid={`option-pt-${i}`} />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold">Opções ({transLang.toUpperCase()})</p>
            <p className="text-xs text-[#0B1D3A]/50">Deixa em branco para usar o PT.</p>
            {(form.options_pt || []).map((_, i) => (
              <input key={i} value={(form[`options_${transLang}`] || [])[i] || ""} onChange={(e) => setOpt(transLang, i, e.target.value)} placeholder={`Opção ${String.fromCharCode(65 + i)} (opcional)`} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid={`option-en-${i}`} />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">Tempo máximo (s)<input type="number" value={form.max_time_seconds} onChange={(e) => setForm({ ...form, max_time_seconds: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 mt-1" data-testid="question-maxtime-input" /></label>
          <label className="text-sm">Penalização por erro (s)<input type="number" value={form.penalty_seconds} onChange={(e) => setForm({ ...form, penalty_seconds: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 mt-1" data-testid="question-penalty-input" /></label>
        </div>

        <label className="flex items-center gap-2.5 text-sm font-semibold text-[#0B1D3A] cursor-pointer select-none" data-testid="question-active-toggle">
          <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#2D9C5A]" />
          Pergunta ativa <span className="text-xs font-normal text-[#0B1D3A]/50">(só as perguntas ativas entram no jogo)</span>
        </label>

        <button type="submit" className="bg-[#0B1D3A] text-white rounded-lg font-bold inline-flex items-center gap-2 px-5 py-2.5" data-testid="question-submit-btn">
          <Plus size={16} /> {submitLabel}
        </button>
      </form>

      <div className="rounded-2xl bg-[#FDFBF7] p-4 sm:p-6 border border-[#0B1D3A]/10 mb-6" data-testid="question-game-preview">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0B1D3A]/45 uppercase tracking-wide"><Eye size={13} /> Pré-visualização no jogo</span>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-full border border-[#0B1D3A]/15 overflow-hidden text-xs font-bold" data-testid="preview-lang-toggle">
              {["pt", "br", "en"].map((lg) => (
                <button key={lg} type="button" onClick={() => setPvLang(lg)} data-testid={`preview-lang-${lg}`}
                  className={`px-3 py-1 transition-colors ${pvLang === lg ? "bg-[#005F73] text-white" : "bg-white text-[#0B1D3A]/60 hover:text-[#0B1D3A]"}`}>{lg.toUpperCase()}</button>
              ))}
            </div>
            <Link to="/admin/quiz-appearance" className="text-xs font-semibold text-[#005F73] hover:underline">Ajustar tamanhos</Link>
          </div>
        </div>
        {pvImageUrl ? (
          <div className="mx-auto rounded-3xl overflow-hidden bg-white shadow-[0_12px_40px_-10px_rgba(11,29,58,0.15)] border border-[#0B1D3A]/5 mb-5"
            style={{ width: `${pvWidthPct}%`, maxWidth: "100%", aspectRatio: pvFixed ? pvAspect.replace("/", " / ") : undefined }}>
            <img src={pvImageUrl} alt="" className={`bg-white ${pvFixed ? "w-full h-full" : "w-full h-auto"}`} style={{ objectFit: pvFit, maxHeight: pvFixed ? undefined : "40vh" }} />
          </div>
        ) : (
          <p className="text-center text-sm text-[#0B1D3A]/40 mb-5">Carrega uma imagem para a veres aqui.</p>
        )}
        <h2 className="font-fredoka font-bold text-[#0B1D3A] mb-5 text-center leading-snug" style={{ fontSize: `${pvQuestionPx}px` }} data-testid="preview-q-text">
          {pvQuestionText || (pvIsEn ? "Your question will appear here…" : "A tua pergunta aparecerá aqui…")}
        </h2>
        {pvOptions.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pvOptions.map(({ o, i }) => {
              const correct = form.correct_index === i;
              const vars = correct
                ? { "--btn-bg": "#2D9C5A", "--btn-dark": "#1f6e40", "--btn-text": "#FFFFFF" }
                : { "--btn-bg": pvAnsPalette.bg, "--btn-dark": pvAnsPalette.dark, "--btn-text": pvAnsPalette.text };
              return (
                <button key={i} type="button" style={{ fontSize: `${pvAnswerPx}px`, ...vars }}
                  className={`home-btn fmt-${pvAnsFormat} fx-${correct ? "none" : pvAnsEffect} px-5 py-4 font-${pvAnsFont} font-semibold`}>
                  <span className="relative z-[1] inline-flex h-8 w-8 items-center justify-center rounded-full font-bold mr-3 bg-white/25" style={{ color: "var(--btn-text)" }}>{String.fromCharCode(65 + i)}</span>
                  <span className="relative z-[1]">{o}</span>
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-[#0B1D3A]/40 mt-3 text-center">A opção verde é a resposta correta. Tamanhos definidos em <b>Aparência do Quiz</b>.</p>
      </div>
    </div>
  );
}
